import { useState } from "react";

import axios from "axios";

import { CertificateCard } from "entities/certificate";
import { Provider } from "entities/provider";
import { Avatar } from "entities/provider/ui/avatar";
import { EncryptedZkCert } from "shared/snap";
import { useGetSnapQuery, useInvokeSnapMutation } from "shared/snap/rq";
import { Button } from "shared/ui/button";
import { Icon } from "shared/ui/icon";
import { Spinner } from "shared/ui/spinner";
import { notifySuccess } from "shared/ui/toast/notify";
import { capitalizeFirstLetter } from "shared/utils";
import { downloadObjectAsJson } from "shared/utils/download-object-as-json";

type Props = {
  certificate: string;
  from: string;
  provider: Provider;
  onError: (error: unknown) => void;
};
export const GenerationSuccessContent = ({
  certificate,
  from,
  provider,
  onError,
}: Props) => {
  const [isDownloaded, setIsDownloaded] = useState(false);
  const [canBack, setCanBack] = useState(false);

  const snapQuery = useGetSnapQuery();
  const mutation = useInvokeSnapMutation("importZkCert");

  const handleDownload = async () => {
    const url = `${import.meta.env.VITE_API_HOST}/download/${certificate}`;
    try {
      const res = await axios.get(url);
      const data = res.data;

      downloadObjectAsJson(
        JSON.stringify(data),
        `${capitalizeFirstLetter(provider)}-zkCert.json`
      );
      setCanBack(true);
      setIsDownloaded(true);
    } catch (err) {
      console.log("Request certificate error: ", err);
    }
  };

  const handleGet = async () => {
    try {
      const res = await fetch(
        `${import.meta.env.VITE_API_HOST}/download/${certificate}`
      );
      const data: EncryptedZkCert = await res.json();

      console.log("EncryptedZkCert:", data);
      await mutation.mutateAsync({
        chainID: parseInt(import.meta.env.VITE_AUTH_URL),
        encryptedZkCert: data,
      });
      setCanBack(true);
      notifySuccess("Import successfully completed");
    } catch (error) {
      console.error(error);
      onError(error);
    }
  };

  const uploadGalaClick = () => {
    const url = from || "https://app.galactica.com/my-certificates";

    window.open(`${url}/?standard=gip3`, "_blank", "noopener,noreferrer");
  };

  const handleTgGet = () => {
    window.open(
      `https://t.me/GalacticaPassportBot/app?startapp=${certificate.replaceAll(":", "_")}`
    );
  };

  let card = null;

  switch (provider) {
    case "twitter":
      card = (
        <CertificateCard
          avatar={<Avatar onlyImage provider="twitter" />}
          className="h-[186px] border-0 bg-gradient-to-tr from-[#5D5D5D] to-[#1B1B1B]"
          issueDate={null}
          title="X.com (Twitter) zkCertificate"
        />
      );
      break;
    case "uniswap":
      card = (
        <CertificateCard
          avatar={<Avatar onlyImage provider="uniswap" />}
          className="h-[186px] border-0 bg-gradient-to-tr from-[#ffe8f7] to-[#f538d0] text-black/60"
          issueDate={null}
          title="Uniswap zkCertificate"
        />
      );
      break;
  }

  return (
    <>
      <header className="mb-5 flex flex-col items-center justify-center">
        <Icon className="h-6 w-9" name="occam" />
        <h3 className="mt-4 text-center text-lg font-medium">
          {provider === "twitter" ? "The X.com (Twitter)" : "Uniswap"}{" "}
          Certificate was successfully generated
        </h3>
      </header>
      {card}
      {from.includes("tg") ? (
        <footer className="mt-5 flex flex-col gap-2 font-medium">
          <Button onClick={handleTgGet}>Get Certificate</Button>
        </footer>
      ) : (
        <footer className="mt-5 flex flex-col gap-2 font-medium">
          {snapQuery.data && (
            <Button
              disabled={mutation.isPending || Boolean(mutation.data)}
              className="font-tt-commons h-11 gap-1.5 text-lg"
              onClick={handleGet}
            >
              {mutation.isPending ? (
                <Spinner className="size-3.5" />
              ) : (
                <Icon name={mutation.data ? "checkCircle" : "metamask"} />
              )}
              Import to Metamask
            </Button>
          )}

          <Button
            disabled={isDownloaded}
            variant="dodgerBlue/10"
            className="font-tt-commons flex h-11 gap-1 text-lg"
            onClick={handleDownload}
          >
            {isDownloaded ? (
              <>
                <Icon name="checkCircle" />
                Certificate downloaded
              </>
            ) : (
              <>
                <Icon name="fileDownload" />
                Get Backup File
              </>
            )}
          </Button>

          <Button
            disabled={!canBack}
            className="flex h-11"
            variant="transparent"
            onClick={uploadGalaClick}
          >
            Back to Galactica
          </Button>
        </footer>
      )}
    </>
  );
};
